import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toItemArray, toSnakeCaseKeys } from '@shared/api/keys';
import type { SalaOption } from '../domain/rack';
import type { RackOfSala, Sala, SalaId, SalaWritePayload } from '../domain/sala';
import type { DatacenterId } from '../domain/datacenter';
import { filterSalas } from '../domain/sala.rules';

// v1 renombró el recurso `salas` a `rooms` y responde camelCase; el dominio conserva `Sala` y los
// DTO su forma `snake_case` (la conversión ocurre al entrar, con toSnakeCaseKeys).
const SalaOptionDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  datacenter_name: z.string().optional(),
  datacenter_id: z.string().optional(),
});

function toDomain(dto: z.infer<typeof SalaOptionDtoSchema>): SalaOption {
  return {
    id: dto.id as SalaOption['id'],
    name: dto.name,
    datacenterName: dto.datacenter_name,
    datacenterId: dto.datacenter_id,
  };
}

// Contrato real observado en spec.md §2/§4 para el resto del CRUD de salas — snake_case y
// opcionales incluidos. Vive solo aquí; nunca cruza a domain/ ni application/.
const SalaDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  datacenter_id: z.string(),
  datacenter_name: z.string().optional(),
  status: z.string().optional(),
  is_warehouse: z.boolean().optional(),
});

const RackOfSalaDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string().optional(),
  room_id: z.string().optional(),
});

function toSalaDomain(dto: z.infer<typeof SalaDtoSchema>): Sala {
  return {
    id: dto.id as SalaId,
    name: dto.name,
    datacenterId: dto.datacenter_id as DatacenterId,
    datacenterName: dto.datacenter_name,
    status: dto.status,
    isWarehouse: dto.is_warehouse,
  };
}

function toRackOfSalaDomain(dto: z.infer<typeof RackOfSalaDtoSchema>): RackOfSala {
  return {
    id: dto.id,
    name: dto.name,
    status: dto.status,
  };
}

function toDto(payload: SalaWritePayload) {
  return {
    datacenterId: payload.datacenterId,
    name: payload.name,
  };
}

export const salaRepository = {
  async getAll(): Promise<SalaOption[]> {
    const { data } = await apiClient.get('/rooms');
    return z
      .array(SalaOptionDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toDomain);
  },

  // El filtro por datacenterId nunca viaja como query param (paridad exacta con
  // SalasListPage.jsx:78-83, que nunca envía datacenter_id al backend) — se aplica EN MEMORIA
  // reusando la misma regla pura de listado (domain/sala.rules.ts::filterSalas). v1 tampoco
  // acepta `status` como query param, así que ese filtro pasa también a memoria.
  async getAllFiltered(params: { status?: string; datacenterId?: string }): Promise<Sala[]> {
    const { data } = await apiClient.get('/rooms');
    const salas = z
      .array(SalaDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toSalaDomain)
      .filter((s) => !params.status || s.status === params.status);
    return filterSalas(salas, { query: '', datacenterId: params.datacenterId ?? 'all' });
  },

  async getById(id: SalaId): Promise<Sala | null> {
    const { data } = await apiClient.get(`/rooms/${id}`);
    if (!data) return null;
    return toSalaDomain(SalaDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // v1 no expone los racks colgando de la sala: se listan desde `/racks` y se filtran por sala.
  async getRacks(id: SalaId): Promise<RackOfSala[]> {
    const { data } = await apiClient.get('/racks');
    return z
      .array(RackOfSalaDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .filter((r) => r.room_id === id)
      .map(toRackOfSalaDomain);
  },

  async create(payload: SalaWritePayload): Promise<Sala> {
    const { data } = await apiClient.post('/rooms', toDto(payload));
    return toSalaDomain(SalaDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  async update(id: SalaId, payload: SalaWritePayload): Promise<Sala> {
    const { data } = await apiClient.patch(`/rooms/${id}`, toDto(payload));
    return toSalaDomain(SalaDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // Deja pasar el error del backend tal cual (spec.md UC-12: sin validación previa) — el
  // mapeo a mensaje legible ocurre en application/.
  async remove(id: SalaId): Promise<void> {
    await apiClient.delete(`/rooms/${id}`);
  },
};
